import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { message, Modal } from 'antd';
import { selectAllBuilders, clearBuilderState, mergeBuilderStates } from '../../Store/slices/appState.slice';
import { useBuilder } from '../../Contexts/BuilderContext';

const useBuilderDrafts = () => {
    const builders = useSelector(selectAllBuilders);
    const { setPortfolioBuilder } = useBuilder();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);

    const handleOpenDraft = (builderId) => {
        setPortfolioBuilder(builders[builderId]);
        navigate('/builders/portfolio');
    };

    const handleDeleteBuilder = (stateId) => {
        dispatch(clearBuilderState({ stateId }));
        setSelectedRowKeys(selectedRowKeys.filter(key => key !== stateId));
    };

    const handleDeleteSelected = () => {
        selectedRowKeys.forEach(stateId => dispatch(clearBuilderState({ stateId })));
        setSelectedRowKeys([]);
    };

    const combineDrafts = (normWeights) => {
        const mergedItems = {};

        selectedRowKeys.forEach(builderId => {
            const builder = builders[builderId];
            const builderWeight = normWeights[builderId] / 100;

            builder.items.forEach(item => {
                if (mergedItems[item.id]) {
                    mergedItems[item.id].wgt += item.wgt * builderWeight;
                } else {
                    mergedItems[item.id] = { ...item, wgt: item.wgt * builderWeight };
                }
            });
        });

        const newBuilderState = {
            stateId: `merged-${Date.now()}`,
            title: 'Merged Draft',
            type: 'merged',
            items: Object.values(mergedItems),
        };

        dispatch(mergeBuilderStates({ newBuilderState, mergedIds: selectedRowKeys }));
        setSelectedRowKeys([]);
    };

    const handleMergeSelected = () => {
        if (selectedRowKeys.length < 2) {
            message.error('Select at least two drafts to merge');
            return;
        }
        setModalOpen(true);
    };

    const onFinishMerge = (normWeights) => {
        setModalOpen(false);
        combineDrafts(normWeights);
        Modal.confirm({
            title: 'Delete old drafts?',
            content: 'Do you want to delete the old drafts that were combined?',
            onOk: () => {
                handleDeleteSelected();
                message.success('Old drafts deleted');
            },
            onCancel: () => {
                message.info('Old drafts retained');
            },
        });
    };

    const handleSelectRow = (stateId) => {
        setSelectedRowKeys(prevSelected => {
            if (prevSelected.includes(stateId)) {
                return prevSelected.filter(key => key !== stateId);
            }
            return [...prevSelected, stateId];
        });
    };

    const data = Object.keys(builders).map(key => ({
        key,
        stateId: builders[key].stateId,
        name: builders[key].title,
        type: builders[key].type,
        items: builders[key].items,
    }));

    return {
        data,
        selectedRowKeys,
        modalOpen,
        handleOpenDraft,
        handleDeleteBuilder,
        handleDeleteSelected,
        handleMergeSelected,
        onFinishMerge,
        handleSelectRow,
        setModalOpen,
    };
};

export default useBuilderDrafts;
